import { useQuery } from "@tanstack/react-query";
import { LockKeyholeIcon } from "lucide-react";

import { workspaceEntryBrandingQuery } from "../api/queries";
import { DocumentMetadata } from "../components/DocumentMetadata";
import { LoginCard } from "../components/LoginCard";
import { useOnlineStatus } from "../components/OfflineState";
import { ThemeToggle } from "../components/ThemeToggle";
import { cn } from "../lib/utils";
import { workspaceAccentStyle } from "../lib/workspace-accent";
import { loginRoute } from "../router";
import type { WorkspaceEntryBrandingResponse } from "../../shared/schemas";

function WorkspaceLoginFooter({ workspaceName }: { workspaceName: string | undefined }) {
  return (
    <div className="workspace-login-footer text-center text-[12px] text-muted-foreground">
      <p className="flex items-center justify-center gap-1.5">
        <LockKeyholeIcon aria-hidden="true" className="size-3" />
        {workspaceName
          ? `Only people at ${workspaceName} can sign in here.`
          : "Secure access to your CCBox workspace."}
      </p>
      {workspaceName ? (
        <p className="mt-6">
          Powered by <span className="font-semibold text-foreground">CCBox</span>
        </p>
      ) : null}
    </div>
  );
}

function WorkspaceLoginPage({
  isOnline,
  requestedOrganizationId,
  workspace,
}: {
  isOnline: boolean;
  requestedOrganizationId: string | undefined;
  workspace: WorkspaceEntryBrandingResponse["workspace"];
}) {
  const faviconHref = workspace?.faviconVersion
    ? `/api/workspace-entry/favicon?v=${workspace.faviconVersion}`
    : undefined;
  return (
    <main
      className={cn(
        "workspace-login-layout relative flex min-h-dvh flex-col bg-background",
        workspace?.primaryColor && "workspace-accent",
      )}
      style={workspaceAccentStyle(workspace?.primaryColor)}
    >
      <DocumentMetadata
        faviconHref={faviconHref}
        title={workspace ? `Sign in · ${workspace.name} Tools` : "CCBox"}
      />
      <div className="login-theme-control absolute z-10">
        <ThemeToggle />
      </div>
      <section
        aria-labelledby="login-title"
        className="workspace-login-main grid flex-1 place-items-center"
      >
        <div className="workspace-login-stack w-full max-w-[360px]">
          <LoginCard
            isOnline={isOnline}
            requestedOrganizationId={requestedOrganizationId}
            workspace={workspace}
          />
          <WorkspaceLoginFooter workspaceName={workspace?.name} />
        </div>
      </section>
    </main>
  );
}

export function LoginPage() {
  const search = loginRoute.useSearch();
  const isOnline = useOnlineStatus();
  const entryBranding = useQuery({ ...workspaceEntryBrandingQuery, enabled: isOnline });
  const workspace = entryBranding.data?.workspace ?? null;
  const waitingForBrand =
    isOnline && window.location.hostname !== "app.ccbox.ai" && entryBranding.isPending;

  if (waitingForBrand) {
    return (
      <main className="workspace-login-layout relative min-h-dvh bg-background">
        <div className="login-theme-control absolute z-10">
          <ThemeToggle />
        </div>
        <p className="sr-only" role="status">
          Loading workspace sign in
        </p>
      </main>
    );
  }

  return (
    <WorkspaceLoginPage
      isOnline={isOnline}
      requestedOrganizationId={search.org}
      workspace={workspace}
    />
  );
}
